import { forwardRef, useState, useEffect } from "react";
import { useIntl } from "react-intl";
import "./TravelersDropdown.scss";
import "../Dropdown.scss";
import Button from "../../ui/Button/Button";
import { boardTypeInfos, commonDivisors, roomTypeInfos } from "../../../utils";
import { normalizeRoomAllocation, pruneRoomAllocationToOptions, roomAllocationIsMeaningful, roomsFromTotals } from "../../../roomAllocation";
import RoomAllocationEditor from "../../RoomAllocationEditor/RoomAllocationEditor";
import { useDispatch, useSelector } from "react-redux";
import { setSearchOption, setSelectedDestination } from "../../../reducers/searchSlice";
import { filteredProperties } from "../../../reducers/filterSlice";

const TravelersDropdown = forwardRef(
  ({ onSelect, onClose, selectedValue }, ref) => {
    const intl = useIntl();
    const dispatch = useDispatch();
    const [adults, setAdults] = useState(selectedValue?.value?.adults || 1);
    const [children, setChildren] = useState(
      selectedValue?.value?.children || 0
    );

    const [rooms, setRooms] = useState(selectedValue?.value?.rooms || 1);
    const [childrenAges, setChildrenAges] = useState(
      selectedValue?.value?.childrenAges || []
    );

    const [multiRooms, setMultiRooms] = useState([]);
    const multiroomEnabled = useSelector((state) => !!state.properties?.providerCapabilities?.multiroom);
    const searchRegions = useSelector((state) => state.search.searchRegions);
    const selectedDestination = useSelector((state) => state.search.selectedDestination);
    const roomTypesFilter = useSelector((state) => state.filter.roomTypes);
    const boardTypesFilter = useSelector((state) => state.filter.boardTypes);

    const roomTypeOptions = (Array.isArray(roomTypesFilter) ? roomTypesFilter : [])
      .map((rt) => roomTypeInfos.find((e) => e.value === rt?.Value))
      .filter(Boolean)
      .sort((a, b) => a.order - b.order)
      .map((e) => ({ value: e.value, label: e.label }));

    const boardTypeOptions = (Array.isArray(boardTypesFilter) ? boardTypesFilter : [])
      .map((bt) => boardTypeInfos.find((e) => e.value === bt?.Value))
      .filter(Boolean)
      .map((e) => ({ value: e.value, label: e.label }));

    useEffect(() => {
      if (!multiroomEnabled) return;
      const region = (Array.isArray(selectedDestination) && selectedDestination.length > 0 && selectedDestination) || (Array.isArray(searchRegions) && searchRegions.length > 0 && searchRegions) || null;
      if (!region || (roomTypeOptions.length > 0 && boardTypeOptions.length > 0)) return;
      if (!(Array.isArray(selectedDestination) && selectedDestination.length > 0)) dispatch(setSelectedDestination(region));
      if (roomTypeOptions.length === 0) dispatch(filteredProperties({ hotel: false, property: "RoomType", update: {} }));
      if (boardTypeOptions.length === 0) dispatch(filteredProperties({ hotel: false, property: "BoardTypes", update: {} }));
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const [allocationMode, setAllocationMode] = useState(!!normalizeRoomAllocation(selectedValue?.value?.roomAllocation));
    const [roomAllocation, setRoomAllocation] = useState(
      normalizeRoomAllocation(selectedValue?.value?.roomAllocation) ||
        roomsFromTotals(selectedValue?.value?.adults || 1, selectedValue?.value?.children || 0, selectedValue?.value?.childrenAges || [], selectedValue?.value?.rooms || 1)
    );

    const rtKey = roomTypeOptions.map((o) => o.value).join(",");
    const btKey = boardTypeOptions.map((o) => o.value).join(",");
    useEffect(() => {
      if (rtKey === "" && btKey === "") return;
      setRoomAllocation((prev) => pruneRoomAllocationToOptions(prev, rtKey ? rtKey.split(",") : [], btKey ? btKey.split(",") : []));
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [rtKey, btKey]);

    const toggleAllocationMode = (checked) => {
      if (checked && !roomAllocationIsMeaningful(normalizeRoomAllocation(roomAllocation))) {
        setRoomAllocation(roomsFromTotals(adults, children, childrenAges, rooms));
      }
      setAllocationMode(checked);
    };

    useEffect(() => {
      setMultiRooms(
        commonDivisors(
          adults * 1,
          children * 1 === 0 ? adults * 1 : children * 1
        )
      );
      
      const maxRooms = adults + children;
      if (rooms > maxRooms) {
        setRooms(maxRooms);
      }
    }, [adults, children]);

    useEffect(() => {
      let newAges = [...childrenAges];

      if (newAges.length > children) {
        newAges = newAges.slice(0, children);
      }

      while (newAges.length < children) {
        newAges.push(0);
      }

      setChildrenAges(newAges);
    }, [children]);

    useEffect(() => {
      if (selectedValue?.value) {
        setAdults(selectedValue.value.adults || 1);
        setChildren(selectedValue.value.children || 0);
        setRooms(selectedValue.value.rooms || 1);
        setChildrenAges(selectedValue.value.childrenAges || 0);
      }
    }, [selectedValue]);

    const increment = (setter, value) => {
      setter(value + 1);
    };

    const decrement = (setter, value) => {
      if (value > 0) {
        setter(value - 1);
      }
    };

    const handleApply = () => {
      const activeAllocation = allocationMode ? normalizeRoomAllocation(roomAllocation) : null;
      const effAdults = activeAllocation ? activeAllocation.reduce((s, r) => s + r.adults, 0) : adults;
      const effChildAges = activeAllocation ? activeAllocation.flatMap((r) => r.childrenAges) : childrenAges;
      const effChildren = activeAllocation ? effChildAges.length : children;
      const effRooms = activeAllocation ? activeAllocation.length : rooms;
      const totalGuests = effAdults + effChildren;

      const roomText = effRooms === 1
        ? intl.formatMessage({ id: 'room' })
        : intl.formatMessage({ id: 'rooms' });

      const guestText = totalGuests === 1
        ? intl.formatMessage({ id: 'person' })
        : intl.formatMessage({ id: 'persons' });

      const label = `${totalGuests} ${guestText}, ${effRooms} ${roomText}`;

      onSelect({
        label,
        value: { adults: effAdults, children: effChildren, rooms: effRooms, childrenAges: effChildAges, roomAllocation: activeAllocation },
      });

      if (onClose) {
        onClose();
      }
    };

    return (
      <div className="main_input-dropdown travelers-dropdown" ref={ref}>
        <div className="travelers-section">
          {multiroomEnabled && (
            <label className="traveler-row">
              <span className="traveler-label">{intl.formatMessage({ id: 'differentRoomOccupancy' })}</span>
              <input type="checkbox" checked={allocationMode} onChange={(e) => toggleAllocationMode(e.target.checked)} />
            </label>
          )}

          {allocationMode ? (
            <RoomAllocationEditor
              rooms={roomAllocation}
              onChange={setRoomAllocation}
              roomTypeOptions={roomTypeOptions.length > 0 ? roomTypeOptions : undefined}
              boardTypeOptions={boardTypeOptions.length > 0 ? boardTypeOptions : undefined}
            />
          ) : (
          <>
          <div className="traveler-row">
            <span className="traveler-label">
              {intl.formatMessage({ id: 'adults' })}
            </span>
            <div className="counter">
              <button
                className="counter-btn"
                onClick={() => decrement(setAdults, adults)}
                disabled={adults <= 1}
              >
                −
              </button>
              <span className="counter-value">{adults}</span>
              <button
                className="counter-btn"
                onClick={() => increment(setAdults, adults)}
                disabled={adults >= 6}
              >
                +
              </button>
            </div>
          </div>

          <div className="traveler-row">
            <span className="traveler-label">
              {intl.formatMessage({ id: 'children' })}
            </span>
            <div className="counter">
              <button
                className="counter-btn"
                onClick={() => decrement(setChildren, children)}
                disabled={children <= 0}
              >
                −
              </button>
              <span className="counter-value">{children}</span>
              <button
                className="counter-btn"
                onClick={() => increment(setChildren, children)}
                disabled={children >= 4}
              >
                +
              </button>
            </div>
          </div>

          <div className="traveler-row">
            <span className="traveler-label">
              {intl.formatMessage({ id: 'rooms' })}
            </span>
            <div className="counter">
              <button
                className="counter-btn"
                onClick={() => decrement(setRooms, rooms)}
                disabled={rooms <= 1}
              >
                −
              </button>
              <span className="counter-value">{rooms}</span>
              <button
                className="counter-btn"
                onClick={() => increment(setRooms, rooms)}
                disabled={rooms >= adults + children}
              >
                +
              </button>
            </div>
          </div>

          {Array.isArray(childrenAges) && childrenAges.length > 0 && (
            <div className="children-ages-section">
              <div className="section-title">
                {intl.formatMessage({ id: 'childrenAges' })}
              </div>
              {childrenAges.map((age, idx) => (
                <div key={idx} className="age-select-wrapper">
                  <label className="age-label">
                    {intl.formatMessage({ id: 'child' })} {idx + 1}
                  </label>
                  <select
                    className="age-select"
                    value={age}
                    onChange={(e) => {
                      let ages = [...childrenAges];
                      ages[idx] = e.target.value * 1;
                      setChildrenAges(ages);
                    }}
                  >
                    <option value={0} disabled>
                      {intl.formatMessage({ id: 'pleaseSelect' })}
                    </option>
                    {[
                      1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17,
                    ].map((ageOption, idxAge) => (
                      <option key={idxAge} value={ageOption}>
                        {ageOption === 1 ? "<2" : ageOption}{" "}
                        {intl.formatMessage({ 
                          id: ageOption === 1 ? 'years' : 'years' 
                        })}
                      </option>
                    ))}
                  </select>
                </div>
              ))}
            </div>
          )}

          {multiRooms.length > 0 && (
            <div className="multi-rooms-section">
              <div className="section-title">
                {intl.formatMessage({ id: 'roomDistribution' })}
              </div>
              {multiRooms.map((roomCount) => {
                let adultsCount = adults * 1;
                let childrenCount = children * 1;
                const adultsPerRoom = adultsCount / roomCount;
                const childrenPerRoom = childrenCount / roomCount;

                return (
                  <div key={roomCount} className="room-option">
                    <label className="room-option-label">
                      <input
                        type="radio"
                        className="room-radio"
                        checked={rooms === roomCount}
                        name="multiroomcheck"
                        id={`multiroomradio${roomCount}`}
                        onChange={() => setRooms(roomCount)}
                      />
                      <div className="room-details">
                        <span className="room-count">
                          {roomCount}{" "}
                          {roomCount > 1
                            ? intl.formatMessage({ id: 'roomsWithEach' })
                            : intl.formatMessage({ id: 'roomWith' })}
                        </span>
                        <span className="guest-distribution">
                          {adultsPerRoom}{" "}
                          {adultsPerRoom > 1
                            ? intl.formatMessage({ id: 'adultsPlural' })
                            : intl.formatMessage({ id: 'adultSingular' })}
                          {childrenCount > 0 && (
                            <>
                              {" "}
                              {intl.formatMessage({ id: 'and' })}{" "}
                              {childrenPerRoom}
                              {childrenPerRoom > 1
                                ? " " + intl.formatMessage({ id: 'childrenPlural' })
                                : " " + intl.formatMessage({ id: 'childSingular' })}
                            </>
                          )}
                        </span>
                      </div>
                    </label>
                  </div>
                );
              })}
            </div>
          )}
          </>
          )}
        </div>

        {(allocationMode ? !roomAllocation.some((r) => r.childrenAges.includes(0)) : !childrenAges.includes(0)) && (
          <Button
            text={intl.formatMessage({ id: 'apply' })}
            onClick={handleApply}
            size={"s"}
            color={"orange"}
            style={"rounded"}
          />
        )}
      </div>
    );
  }
);

export default TravelersDropdown;